import { z } from 'zod';
import { cnpjValido } from './empresa.js';
import { cuid } from './primitives.js';

/** Configuracoes > Empresas (Sessao 18): so SUPERADMIN e ADMIN_TENANT (`empresas.gerenciar`). */

export const criarGrupoSchema = z.object({ nome: z.string().trim().min(1, 'Informe o nome do grupo').max(120) });
export type CriarGrupoDto = z.infer<typeof criarGrupoSchema>;

/** PATCH /gestao/empresas/grupos/:id (Sessao 37, tela Grupos): renomeia. */
export const renomearGrupoSchema = criarGrupoSchema;
export type RenomearGrupoDto = CriarGrupoDto;

/** POST /gestao/empresas/:id/mover (Sessao 37): muda a empresa de grupo (o grupo de destino tem que estar ativo). */
export const moverEmpresaSchema = z.object({ grupoId: cuid });
export type MoverEmpresaDto = z.infer<typeof moverEmpresaSchema>;

/** Empresa nova num grupo existente (`grupoId`) ou num grupo novo (`grupoNovo`): exatamente um dos dois. CNPJ so digitos (14, valido) ou vazio. */
export const criarEmpresaSchema = z
  .object({
    grupoId: cuid.optional(),
    grupoNovo: z.string().trim().min(1).max(120).optional(),
    nome: z.string().trim().min(1, 'Informe o nome da empresa').max(200),
    cnpj: z
      .string()
      .trim()
      .transform((v) => v.replace(/\D/g, ''))
      .refine((v) => v === '' || cnpjValido(v), 'CNPJ invalido (confira os 14 digitos)')
      .optional(),
  })
  .refine((v) => (v.grupoId === undefined) !== (v.grupoNovo === undefined), { message: 'Informe o grupo existente OU o nome de um grupo novo', path: ['grupoId'] });
export type CriarEmpresaDto = z.infer<typeof criarEmpresaSchema>;

export const convidarAdminEmpresaSchema = z.object({
  email: z.string().trim().toLowerCase().email('E-mail invalido').max(200),
  nome: z.string().trim().min(1).max(200).optional(),
});
export type ConvidarAdminEmpresaDto = z.infer<typeof convidarAdminEmpresaSchema>;

export interface EmpresaGestaoResponse {
  id: string;
  grupoId: string;
  nome: string;
  cnpj: string | null;
  ativa: boolean;
  criadaEm: string;
  /** Usuarios com o papel ADMIN_EMPRESA nesta empresa (ativos). */
  administradores: number;
  /** Empresa de demonstracao (Sessao 24). */
  demo: boolean;
}
export interface GrupoGestaoResponse {
  id: string;
  nome: string;
  /** Sessao 37: grupo desativado nao recebe empresas novas. */
  ativo: boolean;
  empresas: EmpresaGestaoResponse[];
}
export interface GestaoEmpresasResponse {
  grupos: GrupoGestaoResponse[];
}

/**
 * POST /gestao/empresas/demo (Sessao 38): CRIAR uma demo nova (`nome` da empresa e o grupo: `grupoId` existente OU `grupoNovo`, exatamente um) ou RECRIAR os dados de uma
 * demo (`empresaId`, `confirmar` = "DEMO"). O tenant pode ter quantas demos quiser; excluir uma demo e a exclusao definitiva de qualquer empresa.
 */
export const criarDemoSchema = z
  .object({
    acao: z.enum(['CRIAR', 'RECRIAR']),
    grupoId: cuid.optional(),
    grupoNovo: z.string().trim().min(1).max(120).optional(),
    nome: z.string().trim().min(1, 'Informe o nome da empresa').max(200).optional(),
    empresaId: cuid.optional(),
    confirmar: z.string().trim().max(10).optional(),
    /**
     * Sessao 51b: REALISTA (padrao) = parametros plausiveis de industria (despesa fixa ~15%, lucro ~12%, divisor >= 0,3); CONTROLE = a demo do CTRL-01
     * (despesa fixa 23% e lucro 20%, divisor 0,085), usada pelos testes de controle.
     */
    conteudo: z.enum(['REALISTA', 'CONTROLE']).default('REALISTA'),
  })
  .superRefine((v, ctx) => {
    if (v.acao === 'CRIAR') {
      if (!v.nome) ctx.addIssue({ code: 'custom', path: ['nome'], message: 'Informe o nome da empresa' });
      if (!v.grupoId && !v.grupoNovo) ctx.addIssue({ code: 'custom', path: ['grupoNovo'], message: 'Informe o nome do grupo (escolha um existente ou digite um novo)' });
      if (v.grupoId && v.grupoNovo) ctx.addIssue({ code: 'custom', path: ['grupoNovo'], message: 'Escolha um grupo existente OU digite um novo, não os dois' });
    } else if (!v.empresaId) ctx.addIssue({ code: 'custom', path: ['empresaId'], message: 'Informe a empresa demo' });
  });
export type CriarDemoDto = z.infer<typeof criarDemoSchema>;
export const PALAVRA_RECRIAR_DEMO = 'DEMO';

/** Andamento da criacao/recriacao de UMA empresa demo (a tela consulta ate terminar). */
export interface DemoStatusResponse {
  /** Id da empresa demo. */
  empresaId: string | null;
  estado: 'NENHUMA' | 'EXECUTANDO' | 'PRONTA' | 'ERRO';
  mensagem: string | null;
  iniciadoEm: string | null;
  terminadoEm: string | null;
}
/** GET /gestao/empresas/demo: as demos do tenant e o andamento de cada uma. */
export interface DemosResponse {
  demos: DemoStatusResponse[];
}

// ---------------------------------------------------------------------------------------------
// Exclusao definitiva de empresa e de grupo (Sessao 38). So `empresas.gerenciar` (ADMIN_TENANT e SUPERADMIN), dentro do proprio tenant.
// ---------------------------------------------------------------------------------------------

/** O que a exclusao apaga, por tipo (a previa mostra; o evento de tenant que sobrevive grava). */
export interface ExclusaoContagens {
  itens: number;
  fichas: number;
  orcamentos: number;
  clientes: number;
  pessoas: number;
  rateios: number;
  precos: number;
  anexos: number;
}
export interface ExclusaoEmpresaPrevia {
  id: string;
  nome: string;
  cnpj: string | null;
  demo: boolean;
  contagens: ExclusaoContagens;
}
/** GET /gestao/empresas/:id/exclusao-previa e /gestao/empresas/grupos/:id/exclusao-previa. */
export interface ExclusaoPreviaResponse {
  alvo: 'EMPRESA' | 'GRUPO';
  id: string;
  /** Nome que o usuario digita para confirmar (da empresa ou do grupo). */
  nome: string;
  empresas: ExclusaoEmpresaPrevia[];
  total: ExclusaoContagens;
}
export const AVISO_EXCLUSAO_DEFINITIVA = 'Exclusão definitiva. Só é possível recuperar pelo backup do banco.';

/** POST /gestao/empresas/:id/excluir: o NOME exato da empresa e a SENHA do proprio usuario (conferida no servidor). */
export const excluirEmpresaSchema = z.object({ nome: z.string().trim().min(1, 'Digite o nome da empresa').max(200), senha: z.string().min(1, 'Digite a sua senha').max(200) });
export type ExcluirEmpresaDto = z.infer<typeof excluirEmpresaSchema>;
/** POST /gestao/empresas/grupos/:id/excluir: alem do nome do grupo e da senha, TODAS as empresas do grupo listadas e confirmadas (grupo vazio: lista vazia). */
export const excluirGrupoSchema = excluirEmpresaSchema.extend({ empresaIds: z.array(cuid).max(500) });
export type ExcluirGrupoDto = z.infer<typeof excluirGrupoSchema>;
export interface ExclusaoResponse {
  alvo: 'EMPRESA' | 'GRUPO';
  empresasExcluidas: number;
  contagens: ExclusaoContagens;
}
/** 5 senhas erradas bloqueiam a exclusao por 15 minutos (por usuario). */
export const EXCLUSAO_MAX_SENHAS_ERRADAS = 5;
export const EXCLUSAO_BLOQUEIO_MIN = 15;
